import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Globe } from 'lucide-react';
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '../i18n';

/**
 * Display names for the supported languages — Phase 4b-2 (8 Jun 2026).
 *
 * Each language is named in its OWN language. This is the
 * international convention (a German speaker scanning the dropdown
 * sees "Deutsch", a French speaker sees "Français") and the right
 * default because language names are proper nouns of language
 * identity — they don't translate. We do NOT route these through
 * t() — there's no key like t('en.name') to look up; the table is
 * the source of truth.
 *
 * Inlined here rather than exported from src/i18n/index.ts because
 * this is currently the only consumer. If a second consumer ever
 * surfaces (e.g. a per-language voice editor on the partner
 * dashboard) we'll lift to src/i18n/index.ts at that point — rule
 * of three.
 */
const LANGUAGE_NAMES: Readonly<Record<SupportedLanguage, string>> =
  Object.freeze({
    en: 'English',
    fr: 'Français',
    de: 'Deutsch',
    es: 'Español',
  });

/**
 * Language selector — Phase 4b-2 (8 Jun 2026).
 *
 * Renders a small pill containing a Globe icon, a native HTML
 * <select> showing the four supported languages each in its own
 * language, and a ChevronDown icon as a visual dropdown affordance.
 *
 * Why a native <select> rather than a custom dropdown:
 *   - Free keyboard navigation (arrow keys, type-to-jump, Enter to
 *     commit) — covered by the browser, not by us.
 *   - Free screen-reader support — announced as "combobox", options
 *     read in order, current selection narrated on change.
 *   - On mobile (especially iOS PWA), the OS opens its native
 *     picker wheel — familiar interaction, accessible by default,
 *     zero custom mobile work.
 *   - Smaller bundle than any third-party combobox library.
 *
 * Custom dropdowns are required when the picker UI itself needs to
 * be branded or contain rich content (icons per option, multi-select,
 * search). None of that applies here — premium for this surface is
 * "looks polished, behaves predictably, accessible by default".
 *
 * Accessibility detail:
 *   - aria-label on the <select> is translated via t() so screen
 *     reader users hear the label in their current language.
 *   - lang="<code>" on each <option> so screen readers pronounce
 *     "Français" with French phonetics, "Deutsch" with German, etc.
 *   - The wrapping <label> provides click-anywhere-to-focus on the
 *     pill area without needing a visible text label.
 *   - Icons carry aria-hidden="true" — they're decorative; the
 *     accessible name is the aria-label on the select.
 *
 * Persistence: i18next.changeLanguage() writes to localStorage via
 * the i18next-browser-languagedetector's caches config (set in
 * src/i18n/index.ts). The choice survives reloads and new sessions
 * without any state management at this component level.
 *
 * Styling: a pill that drops cleanly into a page header. Uses the
 * app's moor-700 focus-ring token for focus state. Background sits
 * at white/80 so the pill reads on the cream JoinPage background
 * without looking like a hard card.
 */
export function LanguageSelector(): JSX.Element {
  const { t, i18n } = useTranslation('common');
  const current = (i18n.resolvedLanguage ?? i18n.language ?? 'en') as SupportedLanguage;
  const label = t('languageSelectorLabel');

  const handleChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    void i18n.changeLanguage(event.target.value);
  };

  return (
    <label className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/80 px-3 py-1.5 text-sm text-slate-700 shadow-sm transition focus-within:border-moor-700 focus-within:ring-2 focus-within:ring-moor-700/20 hover:bg-white">
      <Globe aria-hidden="true" className="h-4 w-4 text-slate-500" />
      <select
        value={current}
        onChange={handleChange}
        aria-label={label}
        className="cursor-pointer appearance-none bg-transparent pr-1 font-medium focus:outline-none"
      >
        {SUPPORTED_LANGUAGES.map((code) => (
          <option key={code} value={code} lang={code}>
            {LANGUAGE_NAMES[code]}
          </option>
        ))}
      </select>
      <ChevronDown aria-hidden="true" className="h-4 w-4 text-slate-500" />
    </label>
  );
}
